/**
 * Editorial building blocks shared by every public page (design_guidelines.md).
 * Quiet luxury: medium-weight headlines, tiny spaced caps labels, hairlines instead of boxes,
 * photos that carry the colour. Motion is restrained: word reveals, drawn hairlines, slow zooms.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Container, EASE, Img, ParallaxImg, Reveal } from "@/components/common/Primitives";
import { HeroMedia, useMedia } from "@/components/common/HeroMedia";
import { imgProps, SIZES } from "@/lib/images";

/* ------------------------------------------------------------------ type */

/**
 * Headline whose words rise out of clipped line boxes, one after another.
 * `inView` (default) plays when scrolled into view; `play` forces immediate playback (heroes).
 */
export const SplitWords = ({ text, lines, as = "span", className = "", delay = 0, stagger = 0.045, duration = 0.9, play, once = true, testid }) => {
  const reduce = useReducedMotion();
  const Comp = motion[as] || motion.span;
  // `lines` = deliberate line breaks (each entry rendered as its own block); otherwise free-flowing words.
  const groups = lines ? lines.map((l) => String(l).split(" ")) : [String(text || "").split(" ")];
  const label = lines ? lines.join(" ") : text;
  const common = reduce ? {} : play === undefined
    ? { initial: "hidden", whileInView: "show", viewport: { once, margin: "-12% 0px -8% 0px" } }
    : { initial: "hidden", animate: play ? "show" : "hidden" };
  let k = 0;
  return (
    <Comp className={className} data-testid={testid} aria-label={label} {...common}>
      {groups.map((words, li) => (
        <span key={li} className={lines ? "block" : "contents"} aria-hidden>
          {words.map((w, i) => {
            const n = k++;
            return (
              <span key={`${w}-${n}`} className="split-line">
                <motion.span
                  className="split-word"
                  variants={reduce ? undefined : { hidden: { y: "110%", opacity: 0 }, show: { y: "0%", opacity: 1 } }}
                  transition={{ duration, delay: delay + n * stagger, ease: [0.16, 1, 0.3, 1] }}
                >
                  {w}
                </motion.span>
                {i < words.length - 1 ? "\u00A0" : ""}
              </span>
            );
          })}
        </span>
      ))}
    </Comp>
  );
};

export const Kicker = ({ children, light = false, className = "", testid }) => (
  <p className={`label-caps ${light ? "text-white/70" : "text-muted-foreground"} ${className}`} data-testid={testid}>{children}</p>
);

/** Section headline – medium weight, calm tracking, words reveal on scroll. `size`: md | lg | xl */
const HEADLINE_SIZE = {
  md: "text-[2rem] sm:text-[2.5rem] lg:text-[3rem]",
  lg: "text-[2.25rem] sm:text-[3rem] lg:text-[3.75rem] xl:text-[4.25rem]",
  xl: "text-[2.5rem] sm:text-[3.5rem] lg:text-[4.5rem] xl:text-[5.5rem]",
};
export const Headline = ({ children, light = false, className = "", testid, as = "h2", split = true, size = "md" }) => {
  const H = as;
  const cls = `${HEADLINE_SIZE[size] || HEADLINE_SIZE.md} font-medium leading-[1.04] tracking-[-0.025em] ${light ? "text-white" : "text-foreground"} ${className}`;
  if (split && typeof children === "string") return <H className={cls} data-testid={testid}><SplitWords text={children} /></H>;
  return <H className={cls} data-testid={testid}>{children}</H>;
};

export const Lead = ({ children, light = false, className = "" }) => (
  <p className={`mt-5 max-w-[54ch] text-[1.0625rem] leading-[1.65] sm:text-lg ${light ? "text-white/85" : "text-muted-foreground"} ${className}`}>{children}</p>
);

export const TextLink = ({ to, href, onClick, children, testid, className = "", light = false, external = false }) => {
  const inner = (
    <>
      <span className="link-grow">{children}</span>
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={1.5} />
    </>
  );
  const cls = `group inline-flex items-center gap-2 text-[0.9375rem] font-medium ${light ? "text-white" : "text-foreground"} ${className}`;
  if (href) return <a href={href} className={cls} data-testid={testid} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>{inner}</a>;
  if (onClick) return <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
  return <Link to={to} className={cls} data-testid={testid}>{inner}</Link>;
};

/** Hairline that draws itself in from the left when scrolled into view. */
export const RuleDraw = ({ className = "", light = false, delay = 0 }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      aria-hidden
      className={`rule-draw h-px w-full ${light ? "bg-white/20" : "bg-border"} ${className}`}
      initial={reduce ? false : { scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    />
  );
};

/** Editorial section header: kicker + headline + lead left, quiet text link right. */
export const SectionHead = ({ kicker, title, lead, action, to, href, onAction, testid, light = false, className = "", size = "md" }) => (
  <div className={`flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-12 ${className}`} data-testid={testid}>
    <div className={size === "md" ? "max-w-2xl" : "max-w-3xl"}>
      {kicker && <Kicker light={light} className="mb-5">{kicker}</Kicker>}
      <Headline light={light} size={size}>{title}</Headline>
      {lead && <Lead light={light}>{lead}</Lead>}
    </div>
    {action && (to || href || onAction) && <TextLink to={to} href={href} onClick={onAction} light={light} testid={testid ? `${testid}-action` : undefined} className="shrink-0 sm:pb-1.5">{action}</TextLink>}
  </div>
);

/** Hairline definition rows – the quiet alternative to icon lists and cards. */
export const Rows = ({ rows, testid, light = false, labelWidth = "sm:grid-cols-[9rem_1fr]", className = "", size = "md" }) => (
  <dl className={`divide-y border-y ${light ? "divide-white/15 border-white/15" : "divide-border border-border"} ${className}`} data-testid={testid}>
    {rows.map(([t, body], i) => (
      <div key={`${t}-${i}`} className={`grid gap-1 ${size === "lg" ? "py-5" : "py-4"} ${labelWidth} sm:gap-6`}>
        <dt className={`${size === "lg" ? "text-base" : "text-[0.9375rem]"} font-medium ${light ? "text-white" : "text-foreground"}`}>{t}</dt>
        <dd className={`${size === "lg" ? "text-base" : "text-[0.9375rem]"} leading-relaxed tabular ${light ? "text-white/75" : "text-muted-foreground"}`}>{body}</dd>
      </div>
    ))}
  </dl>
);

/** Small index numeral with a short hairline – "01 ———". */
export const Index = ({ n, label, className = "" }) => (
  <p className={`index-num flex items-center gap-3 text-muted-foreground ${className}`}>
    <span className="tnum">{String(n).padStart(2, "0")}</span>
    <span className="h-px w-6 bg-border" aria-hidden />
    {label && <span className="label-caps">{label}</span>}
  </p>
);

/** Large photo + text, alternating left/right. The workhorse of every page. */
export const EditorialRow = ({ src, position = "50% 50%", title, body, flip = false, index, label, testid, aspect = "aspect-[4/3] lg:aspect-[16/10]", children, to, eager = false, parallax = true }) => {
  const image = parallax
    ? <ParallaxImg src={src} alt={title} className={aspect} position={position} range={4} sizes={SIZES.half} eager={eager} />
    : <Img src={src} alt={title} className={`img-zoom ${aspect}`} position={position} sizes={SIZES.half} eager={eager} />;
  return (
    <article className="grid items-center gap-7 lg:grid-cols-12 lg:gap-12" data-testid={testid}>
      <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
        {to ? <Link to={to} className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4" aria-label={title}>{image}</Link> : image}
      </div>
      <div className={`lg:col-span-5 ${flip ? "lg:order-1 lg:pr-4" : "lg:pl-4"}`}>
        {index !== undefined && <Index n={index} label={label} />}
        <h3 className="mt-5 text-[1.75rem] font-medium leading-[1.1] tracking-[-0.02em] text-foreground sm:text-[2rem]">{title}</h3>
        {body && <p className="mt-3 max-w-[46ch] text-[1.0625rem] leading-[1.6] text-muted-foreground">{body}</p>}
        {children}
      </div>
    </article>
  );
};

/* ------------------------------------------------------------------ horizontal gallery */

/**
 * Scroll-snap gallery that bleeds off the right edge. Big photos, numbered captions, a thin
 * progress line and two quiet arrows. Native scrolling (drag/trackpad/touch) – no carousel library.
 */
export const HGallery = ({ items = [], testid = "hgallery", className = "", onItemClick, renderItem, itemClass }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  const [edges, setEdges] = useState({ start: true, end: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const on = () => {
      const max = el.scrollWidth - el.clientWidth;
      setProgress(max > 0 ? el.scrollLeft / max : 0);
      setEdges({ start: el.scrollLeft < 8, end: el.scrollLeft > max - 8 });
    };
    on();
    el.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { el.removeEventListener("scroll", on); window.removeEventListener("resize", on); };
  }, [items.length]);
  const step = (dir) => {
    const el = ref.current;
    if (!el) return;
    const card = el.querySelector("[data-gallery-item]");
    const w = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.7;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };
  return (
    <div className={className} data-testid={testid}>
      <div ref={ref} className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:gap-6 sm:px-8 lg:-mx-10 lg:px-10" style={{ scrollPaddingLeft: "1.25rem" }}>
        {items.map((it, i) => (
          <figure key={it.key || it.src || i} data-gallery-item className={`shrink-0 snap-start ${itemClass || `w-[82vw] sm:w-[58vw] lg:w-[46vw] xl:w-[640px] ${it.tall ? "lg:w-[34vw] xl:w-[460px]" : ""}`}`} data-testid={`${testid}-item-${i}`}>
            {renderItem ? renderItem(it, i) : (
              <>
                {onItemClick ? (
                  <button type="button" onClick={() => onItemClick(i)} className="block w-full cursor-zoom-in text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4" aria-label={it.title} data-testid={`${testid}-open-${i}`}>
                    <Img src={it.src} alt={it.title} className={`img-zoom ${it.tall ? "aspect-[4/5]" : "aspect-[3/2]"}`} position={it.position} sizes={SIZES.half} />
                  </button>
                ) : (
                  <Img src={it.src} alt={it.title} className={`img-zoom ${it.tall ? "aspect-[4/5]" : "aspect-[3/2]"}`} position={it.position} sizes={SIZES.half} />
                )}
                <figcaption className="mt-5 flex items-baseline gap-4">
                  <span className="index-num text-muted-foreground tnum">{String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <span className="block text-[1.25rem] font-medium leading-snug tracking-[-0.01em] text-foreground">{it.title}</span>
                    {it.body && <span className="mt-1.5 block max-w-[44ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{it.body}</span>}
                  </span>
                </figcaption>
              </>
            )}
          </figure>
        ))}
        <div className="w-px shrink-0" aria-hidden />
      </div>
      <div className="mt-8 flex items-center gap-6">
        <div className="relative h-px flex-1 bg-border" aria-hidden>
          <div className="absolute inset-y-0 left-0 bg-foreground transition-[width] duration-150" style={{ width: `${Math.max(8, progress * 100)}%` }} />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => step(-1)} disabled={edges.start} className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border text-foreground transition-colors hover:border-foreground/60 disabled:opacity-30 disabled:hover:border-border" aria-label="Previous" data-testid={`${testid}-prev`}><ArrowLeft className="h-4 w-4" strokeWidth={1.5} /></button>
          <button type="button" onClick={() => step(1)} disabled={edges.end} className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border text-foreground transition-colors hover:border-foreground/60 disabled:opacity-30 disabled:hover:border-border" aria-label="Next" data-testid={`${testid}-next`}><ArrowRight className="h-4 w-4" strokeWidth={1.5} /></button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ page hero */

/**
 * Full-bleed photo hero for content pages. Marks itself with `data-photo-hero` so the header
 * knows it can sit transparent on top. Copy bottom-left over a strong bottom + left scrim so the
 * type stays legible on any photo. Slow settle-zoom, parallax and copy fade on scroll.
 */
export const PageHero = ({ title, lead, kicker, image, alt = "", position = "50% 50%", video, videoLabel, children, testid, className = "", height = "h-[78svh] min-h-[560px] max-h-[900px]", titleClass = "" }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const desktop = useMedia();
  const film = !!video && desktop && !reduce;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "12%"]);
  const o = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const up = (delay) => ({ initial: reduce ? false : { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, delay, ease: EASE } });
  return (
    <section ref={ref} className={`relative overflow-hidden bg-fjord ${height} ${className}`} data-testid={testid} data-photo-hero>
      <motion.div style={{ y }} className="absolute inset-x-0 -top-[6%] h-[112%] will-change-transform">
        {video ? (
          <HeroMedia image={image} alt={alt} position={position} video={video} enabled={film} label={videoLabel} testid={testid ? `${testid}-media` : "page-hero-media"} />
        ) : (
          <motion.div initial={reduce ? false : { scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 7, ease: [0.16, 1, 0.3, 1] }} className="h-full w-full origin-center">
            <img {...imgProps(image, SIZES.full)} alt={alt} className="h-full w-full object-cover" style={{ objectPosition: position }} fetchPriority="high" decoding="async" data-testid={testid ? `${testid}-image` : undefined} />
          </motion.div>
        )}
      </motion.div>
      <div className="scrim-nav pointer-events-none absolute inset-x-0 top-0 h-44" aria-hidden />
      <div className="scrim-hero-strong pointer-events-none absolute inset-x-0 bottom-0 h-[72%]" aria-hidden />
      <div className="scrim-hero-left pointer-events-none absolute inset-0" aria-hidden />
      <Container className="relative flex h-full flex-col justify-end pb-10 sm:pb-14 lg:pb-16">
        <motion.div style={{ opacity: o }}>
          {kicker && <motion.p {...up(0.1)} className="label-caps hero-shadow text-white/75" data-testid={testid ? `${testid}-kicker` : undefined}>{kicker}</motion.p>}
          <h1 className={`hero-shadow mt-5 max-w-5xl text-[2.625rem] font-medium leading-[1.0] tracking-[-0.03em] text-white sm:text-[3.5rem] lg:whitespace-nowrap lg:text-[clamp(3.5rem,4.6vw,4.5rem)] ${titleClass}`} data-testid={testid ? `${testid}-title` : undefined}>
            <SplitWords text={title} play delay={0.15} />
          </h1>
          {lead && <motion.p {...up(0.45)} className="hero-shadow mt-6 max-w-[40rem] text-[1.0625rem] leading-[1.6] text-white/90 sm:text-lg lg:text-[1.1875rem]" data-testid={testid ? `${testid}-lead` : undefined}>{lead}</motion.p>}
          {children && <motion.div {...up(0.6)}>{children}</motion.div>}
        </motion.div>
      </Container>
    </section>
  );
};

/** Full-bleed closing photo band with headline + CTAs – ends every public page the same calm way. */
export const ClosingBand = ({ title, lead, image = "/images/pier-wide.jpg", position = "50% 62%", alt = "", children, testid = "closing-band", height = "h-[72svh] max-h-[760px] min-h-[480px]", className = "", size = "md" }) => (
  <section data-testid={testid} className={className}>
    <div className={`relative overflow-hidden bg-fjord ${height}`}>
      <ParallaxImg src={image} alt={alt || title} className="absolute inset-0 h-full w-full" radius="rounded-none" position={position} range={6} sizes={SIZES.full} />
      <div className="scrim-hero-strong pointer-events-none absolute inset-x-0 bottom-0 h-[75%]" aria-hidden />
      <Container className="relative flex h-full flex-col justify-end pb-10 sm:pb-14 lg:pb-16">
        <Reveal className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className={size === "md" ? "max-w-xl" : "max-w-3xl"}>
            <Headline light size={size} className="hero-shadow">{title}</Headline>
            {lead && <Lead light className="hero-shadow">{lead}</Lead>}
          </div>
          {children && <div className="flex flex-wrap gap-3">{children}</div>}
        </Reveal>
      </Container>
    </div>
  </section>
);

/** Quiet success panel used after every form. */
export const Success = ({ title, body, testid, children }) => (
  <div className="border-t border-foreground pt-6" data-testid={testid}>
    <p className="label-caps text-muted-foreground">OK</p>
    <h3 className="mt-3 text-[1.75rem] font-medium leading-tight tracking-[-0.02em] text-foreground">{title}</h3>
    {body && <p className="mt-3 max-w-[50ch] text-[1.0625rem] leading-relaxed text-muted-foreground">{body}</p>}
    {children}
  </div>
);

/** Form field wrapper – label above, hint below. */
export const Field = ({ id, label, hint, children, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <label htmlFor={id} className="block text-sm font-medium text-foreground">{label}</label>
    {children}
    {hint && <p className="text-xs leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

export const INPUT_CLS = "h-12 rounded-md border-input bg-card px-4 text-[0.9375rem] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";
export const TEXTAREA_CLS = "rounded-md border-input bg-card px-4 py-3 text-[0.9375rem] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";
